import type { PropsWithChildren, ReactNode } from 'react'
import Markdown from 'markdown-to-jsx'

import classes from './AnswerMarkdown.module.scss'

/** Model-authored links may only point at the web or an inbox. */
const SAFE_HREF = /^(?:https?:|mailto:)/i

/**
 * Blocks outside the supported subset keep their words and lose their chrome, so
 * an unexpected construct reads as prose instead of rendering unstyled.
 */
const Plain = ({ children }: PropsWithChildren): ReactNode => children

const Paragraph = ({ children }: PropsWithChildren) => (
  <p className={classes.paragraph}>{children}</p>
)

/**
 * Headings look like headings but are not `<h*>` elements: the model picks levels
 * unpredictably, and injecting them would corrupt the host page's heading outline
 * for screen-reader users. All six levels render alike.
 */
const HeadingLabel = ({ children }: PropsWithChildren) => (
  <p className={classes.heading}>{children}</p>
)

// A plain `<a>` rather than Lemonade's `ExternalLink`, which is a flex container
// with a string-only label — both wrong for a link sitting inline in a sentence.
const Link = ({
  children,
  href,
}: PropsWithChildren<{ href?: string }>): ReactNode =>
  href && SAFE_HREF.test(href) ? (
    <a
      className={classes.link}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ) : (
    children
  )

/** Images are never fetched — only their alt text survives. */
const AltTextOnly = ({ alt }: { alt?: string }): ReactNode => alt

const OVERRIDES = {
  p: Paragraph,
  h1: HeadingLabel,
  h2: HeadingLabel,
  h3: HeadingLabel,
  h4: HeadingLabel,
  h5: HeadingLabel,
  h6: HeadingLabel,
  a: Link,
  img: AltTextOnly,
  code: ({ children }: PropsWithChildren) => (
    <code className={classes.code}>{children}</code>
  ),
  pre: ({ children }: PropsWithChildren) => (
    <pre className={classes.codeBlock}>{children}</pre>
  ),
  ul: ({ children }: PropsWithChildren) => (
    <ul className={classes.list}>{children}</ul>
  ),
  ol: ({ children }: PropsWithChildren) => (
    <ol className={classes.list}>{children}</ol>
  ),
  blockquote: Plain,
  table: Plain,
  thead: Plain,
  tbody: Plain,
  tr: ({ children }: PropsWithChildren) => (
    <p className={classes.paragraph}>{children}</p>
  ),
  th: ({ children }: PropsWithChildren) => (
    <span className={classes.plainCell}>{children}</span>
  ),
  td: ({ children }: PropsWithChildren) => (
    <span className={classes.plainCell}>{children}</span>
  ),
}

// `forceBlock` keeps a one-word answer a paragraph, so layout doesn't shift as the
// turn streams in; raw HTML parsing is off so model output can never inject markup.
const OPTIONS = {
  forceBlock: true,
  disableParsingRawHTML: true,
  overrides: OVERRIDES,
}

export type AnswerMarkdownProps = Readonly<{
  content: string
  testId?: string
}>

/**
 * Renders an assistant turn's prose as a deliberately narrow markdown subset:
 * paragraphs, line breaks, bold, italic, inline code, links, and bulleted or
 * numbered lists. Headings become bold labels; images, tables, block quotes and
 * raw HTML degrade to their text. Partial markdown mid-stream (`**bo`) renders as
 * the literal characters it currently is.
 *
 * The subset is a contract with the answering agent's prompt —
 * see `docs/answer-markdown.md`.
 */
export function AnswerMarkdown({
  content,
  testId,
}: AnswerMarkdownProps): ReactNode {
  return (
    <div className={classes.prose} data-testid={testId}>
      <Markdown options={OPTIONS}>{content}</Markdown>
    </div>
  )
}
